import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { create } from 'zustand';
import type { RealtimeEvent } from '@cw/shared';

/** Estado da conexão (para indicar "ao vivo" na interface). */
export const useRealtimeStatus = create<{ live: boolean; set: (live: boolean) => void }>((set) => ({
  live: false,
  set: (live) => set({ live }),
}));

type Message = { type: string; connId?: string; [k: string]: unknown };
const listeners = new Set<(m: Message) => void>();
let socket: WebSocket | null = null;
let connId: string | null = null;

/** Escuta mensagens do tempo real (boards: mudanças, presença, cursores). Devolve a função de parar. */
export function onRealtimeMessage(fn: (m: Message) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Envia ao servidor, se conectado. */
export function sendRealtime(msg: object): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
}

/** Identificador desta conexão (o servidor não devolve a quem enviou as próprias mudanças). */
export function realtimeConnId(): string | null {
  return socket?.readyState === WebSocket.OPEN ? connId : null;
}

/** Que dados recarregar para cada tipo de mudança. Os prefixos cobrem listas e detalhes. */
const KEYS: Record<string, string[][]> = {
  inbox: [['inbox'], ['notifications']],
  task: [['tasks'], ['projects'], ['calendar'], ['portal'], ['finance'], ['inbox']],
  project: [['projects'], ['clients']],
  client: [['clients']],
  contact: [['clients']],
  brand_asset: [['clients']],
  file: [
    ['clients'],
    ['projects'],
    ['tasks'],
    ['requests'],
    ['library'],
    ['portal'],
    ['approvals'],
    ['files'],
  ],
  request: [['requests'], ['tasks'], ['portal'], ['inbox']],
  event: [['calendar']],
  board: [['boards']],
  moodboard: [['boards']],
  note: [['notes']],
  storyboard: [['storyboards']],
  invoice: [['finance']],
  time: [['time']],
  contract: [['finance']],
};

/**
 * Conexão de tempo real (WebSocket): o servidor avisa que algo mudou e a interface recarrega
 * só o que é afetado. Reconecta sozinha, com espera crescente.
 */
export function useRealtime(): void {
  const qc = useQueryClient();
  useEffect(() => {
    let ws: WebSocket | null = null;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const pending = new Set<string>();
    let flushTimer: ReturnType<typeof setTimeout> | undefined;

    const flush = () => {
      for (const k of pending) qc.invalidateQueries({ queryKey: JSON.parse(k) as string[] });
      pending.clear();
    };

    const connect = () => {
      const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/realtime`;
      const self = new WebSocket(url);
      ws = self;
      socket = self;
      // Só a conexão atual mexe no estado (em desenvolvimento o StrictMode abre e descarta uma).
      const current = () => socket === self && !stopped;
      self.onopen = () => {
        if (!current()) return;
        retry = 0;
        useRealtimeStatus.getState().set(true);
      };
      self.onmessage = (msg) => {
        if (!current()) return;
        const data = JSON.parse(String(msg.data)) as Message & { event?: RealtimeEvent };
        if (data.type === 'ready') connId = data.connId ?? null;
        if (data.type === 'notification') {
          void qc.invalidateQueries({ queryKey: ['notifications'] });
          toast(String(data.title ?? 'Nova notificação'));
          return;
        }
        if (data.type !== 'activity') {
          for (const fn of listeners) fn(data);
          return;
        }
        if (!data.event) return;
        const e = data.event;
        const keys = KEYS[e.entityType] ?? (e.action.startsWith('comment.') ? [['comments'], ['tasks']] : []);
        for (const k of keys) pending.add(JSON.stringify(k));
        if (e.action.startsWith('comment.')) pending.add(JSON.stringify(['comments']));
        // Junta rajadas de mudanças em uma recarga só.
        clearTimeout(flushTimer);
        flushTimer = setTimeout(flush, 250);
      };
      self.onclose = (ev) => {
        if (!current()) return;
        useRealtimeStatus.getState().set(false);
        connId = null;
        if (ev.code === 1008 || ev.code === 4001) return;
        timer = setTimeout(connect, Math.min(30_000, 1000 * 2 ** retry++));
      };
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(flushTimer);
      // Fechar durante a abertura gera erro no console: espera abrir e então fecha.
      if (ws?.readyState === WebSocket.CONNECTING) ws.onopen = () => ws?.close();
      else ws?.close();
    };
  }, [qc]);
}
